import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@symphenia/ui-components'
import { Button } from '@/components/ui/button'
import { POPUP_BLOCKED_EVENT } from '@/lib/download'

/**
 * Der Mandant hat „PDF im Browser öffnen" eingestellt, der Popup-Blocker hat den
 * Tab verhindert.
 *
 * Der Dialog erklärt beides: dass die Datei trotzdem da ist (sie wurde
 * heruntergeladen), und wie sich die Freigabe für genau diese Seite setzen
 * lässt. Er hängt einmal im App-Layout und hört auf das Ereignis aus
 * `downloadFile()` — damit gilt er für jeden PDF-Weg der Anwendung, auch für
 * die Monatsberichte.
 */

type Browser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'

function detectBrowser(): Browser {
  const ua = navigator.userAgent
  // Reihenfolge zählt: Edge und Chrome tragen beide "Chrome" im User-Agent,
  // Safari steht zusätzlich in Chrome — der spezifischste Treffer zuerst.
  if (/Edg\//.test(ua)) return 'edge'
  if (/OPR\/|Opera/.test(ua)) return 'other'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

/** Anzeigename je Browser; für unbekannte gibt es einen übersetzten Platzhalter. */
const BROWSER_NAMES: Record<Exclude<Browser, 'other'>, string> = {
  chrome: 'Chrome',
  edge: 'Edge',
  firefox: 'Firefox',
  safari: 'Safari',
}

export function PopupBlockedDialog() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [filename, setFilename] = useState<string | null>(null)

  useEffect(() => {
    const onBlocked = (event: Event) => {
      const detail = (event as CustomEvent<{ filename?: string }>).detail
      setFilename(detail?.filename ?? null)
      setOpen(true)
    }
    window.addEventListener(POPUP_BLOCKED_EVENT, onBlocked)
    return () => window.removeEventListener(POPUP_BLOCKED_EVENT, onBlocked)
  }, [])

  if (!open) return null

  const browser = detectBrowser()
  const host = window.location.host
  const browserName =
    browser === 'other' ? t('popupBlocked.browserFallback') : BROWSER_NAMES[browser]
  const steps = [1, 2, 3].map((i) => t(`popupBlocked.steps.${browser}${i}`, { host }))

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      title={t('popupBlocked.title')}
      size="md"
      footer={
        <div className="flex justify-end">
          <Button onClick={() => setOpen(false)}>{t('popupBlocked.understood')}</Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-foreground">
        <p>{t('popupBlocked.intro', { browser: browserName })}</p>

        <p className="rounded-md border border-border bg-muted/40 px-3 py-2">
          <strong>{t('popupBlocked.fileIsThere')}</strong> {t('popupBlocked.fileIsThereText')}
          {filename ? (
            <>
              {' '}
              <code className="text-xs">{filename}</code>
            </>
          ) : null}
        </p>

        <div>
          <p className="mb-2 font-medium">{t('popupBlocked.howTo')}</p>
          <ol className="list-decimal space-y-1.5 pl-5 text-muted-foreground">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        <p className="text-xs text-muted-foreground">{t('popupBlocked.adminHint')}</p>
      </div>
    </Modal>
  )
}
